import type { ReactNode } from 'react'
import { CalendarDays, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ACTIVITY_OPTIONS,
  DELETED_OPTIONS,
  MODE_OPTIONS,
  PLAN_OPTIONS,
  RANGE_PRESETS,
  STATUS_OPTIONS,
  TAB_FILTERS,
  type Filters,
  type InsightsOptions,
  type InsightsTab,
} from '@/lib/asistan'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      {children}
    </label>
  )
}

const inputClass =
  'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500'

function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
}) {
  return (
    <select className={cn(inputClass, 'min-w-[11rem]')} value={value} onChange={(e) => onChange(e.target.value)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

// `days` and a custom from/to are mutually exclusive, so choosing one clears the
// other — otherwise the endpoint silently prefers `days` and the date boxes look
// broken rather than overridden.
export default function FilterBar({
  tab,
  filters,
  options,
  onPatch,
  onApply,
  onReset,
  onSearch,
  loading,
  activeCount,
}: {
  tab: InsightsTab
  filters: Filters
  options?: InsightsOptions
  onPatch: (patch: Partial<Filters>) => void
  onApply: () => void
  onReset: () => void
  onSearch: (key: 'email' | 'assistant', value: string) => void
  loading: boolean
  activeCount: number
}) {
  const visible = TAB_FILTERS[tab]
  const has = (key: keyof Filters) => visible.includes(key)
  const accountTypeOptions = (options?.accountTypes ?? []).map((row) => ({
    value: row.value,
    label: `${row.value} (${row.count})`,
  }))
  const assistantOptions = (options?.assistants ?? []).map((row) => ({ value: row.value, label: `${row.value} (${row.count})` }))

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <SlidersHorizontal className="h-4 w-4 text-gray-400" />
          Filters
          {activeCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
              {activeCount} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 disabled:opacity-40"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={loading}
            className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>
      </div>

      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault()
          onApply()
        }}
      >
        {has('email') && (
          <Field label="User">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={filters.email}
                onChange={(e) => onSearch('email', e.target.value)}
                placeholder="email or name"
                className={cn(inputClass, 'w-full pl-8')}
              />
            </div>
          </Field>
        )}

        {has('assistant') && (
          <Field label="Assistant">
            <input
              type="search"
              list="asistan-assistants"
              value={filters.assistant}
              onChange={(e) => onSearch('assistant', e.target.value)}
              placeholder="any assistant"
              className={cn(inputClass, 'w-full')}
            />
            <datalist id="asistan-assistants">
              {assistantOptions.map((option) => (
                <option key={option.value} value={option.value} />
              ))}
            </datalist>
            {options?.assistantsTruncated && (
              <span className="text-xs text-gray-400">suggestions: the {assistantOptions.length} busiest — type to search all</span>
            )}
          </Field>
        )}

        {has('days') && (
          <Field label="Range">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <select
                className={cn(inputClass, 'w-full')}
                value={filters.days}
                onChange={(e) => onPatch({ days: e.target.value, from: '', to: '' })}
              >
                {RANGE_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>
          </Field>
        )}

        {has('from') && (
          <Field label="Custom from">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => onPatch({ from: e.target.value, days: '' })}
              className={cn(inputClass, 'w-full')}
            />
          </Field>
        )}

        {has('to') && (
          <Field label="Custom to">
            <input
              type="date"
              value={filters.to}
              onChange={(e) => onPatch({ to: e.target.value, days: '' })}
              className={cn(inputClass, 'w-full')}
            />
          </Field>
        )}

        {has('account_type') && (
          <Field label="Account type">
            <Select
              value={filters.account_type}
              onChange={(value) => onPatch({ account_type: value })}
              options={accountTypeOptions}
              placeholder="Any type"
            />
          </Field>
        )}

        {has('plan') && (
          <Field label="Account state">
            <Select value={filters.plan} onChange={(value) => onPatch({ plan: value })} options={PLAN_OPTIONS} />
          </Field>
        )}

        {has('activity') && (
          <Field label="Calling activity">
            <Select value={filters.activity} onChange={(value) => onPatch({ activity: value })} options={ACTIVITY_OPTIONS} />
          </Field>
        )}

        {has('mode') && (
          <Field label="Call mode">
            <Select value={filters.mode} onChange={(value) => onPatch({ mode: value })} options={MODE_OPTIONS} />
          </Field>
        )}

        {has('status') && (
          <Field label="Outcome">
            <Select value={filters.status} onChange={(value) => onPatch({ status: value })} options={STATUS_OPTIONS} />
          </Field>
        )}

        {has('deleted') && (
          <Field label="Deletion">
            <Select value={filters.deleted} onChange={(value) => onPatch({ deleted: value })} options={DELETED_OPTIONS} />
          </Field>
        )}

        {has('limit') && (
          <Field label="Rows">
            <select className={cn(inputClass, 'w-full')} value={filters.limit} onChange={(e) => onPatch({ limit: e.target.value })}>
              {['100', '250', '500', '1000'].map((value) => (
                <option key={value} value={value}>
                  Up to {value}
                </option>
              ))}
            </select>
          </Field>
        )}
      </form>

      {activeCount > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <span className="text-xs text-gray-500">Showing only rows matching:</span>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
          >
            clear all filters
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  )
}
